import React, { memo } from 'react';

import { useSelector } from 'react-redux';

import clsx from 'clsx';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Box, Heading, Section } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import { checkPricingExists } from '@/utilities/pricingUtils';

import { inventoryProductClick } from '@/analytics/inventoryAnalyticsHandlers';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import LazyComponent from '@/components/LazyComponent';
import PriceAdvisorReport from '@/components/PriceAdvisorReport';
import PricingDetails from '@/components/vehicledetails/PricingDetails';

import ShowcaseAdSlotContainer from './ShowcaseAdSlotContainer';

export function PricingDetailsContainer({
    emailCTAProps,
    disablePriceBreakdown,
}) {

    const device = useDevice();
    const isSm = device?.is?.sm ?? false;

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const hidePriceBreakdown = listing?.dealerConfig?.hidePriceBreakDown ?? false;

    const {
        owner = {},
        priceAdvisorEnabled,
        type,
        paymentServices = {},
        productTiles = [],
        pricingDetail = {},
    } = listing;

    const parTile = productTiles.find((tile) => tile?.epn === 'autovisor');

    const {
        price_advisor: [enablePriceAdvisor],
        price_advisor_state_exclusions: [priceAdvisorStateExclusions, {
            opt_out_states: isStateOptedOut,
        }],
        price_details: [enablePriceDetails],
        thirdparty: [enableThirdParty],
    } = useFeatures([
        'price_advisor',
        'price_advisor_state_exclusions',
        'price_details',
        'thirdparty',
    ]);

    const handlePriceAdvisorReportClick = (event) => {
        sendClick(inventoryProductClick, event, { epn: parTile?.epn });
    };

    // Turn off price advisor if optimizely feature kill switch is active
    const isPriceAdvisorVisible = enableThirdParty && enablePriceAdvisor && priceAdvisorEnabled;
    // Display price breakdown for all non DR Dealers and only for new cars for DR dealers respecting Accelerate configuration for NDS breakdown display
    const isPriceBreakdownVisible = (paymentServices.digitalRetailingType !== 'accelerate') || (type === 'New' && disablePriceBreakdown);
    const isDealerAdjustmentVisible = (paymentServices.digitalRetailingType !== 'accelerate') && type === 'New';
    const hasPrice = checkPricingExists(pricingDetail);

    // Display pricing details except on the FBA site and in a state with restrictions.(e.g. TX), and at least 1 pricing elements must exist
    const displayPricingDetails = (!priceAdvisorStateExclusions || (priceAdvisorStateExclusions && !isStateOptedOut.includes(owner?.location?.address?.state)))
        && (isPriceAdvisorVisible || listing?.pricingDetail?.dealIndicator || listing?.pricingHistory?.length > 0);

    const zip = owner?.location?.address?.zip ?? '';

    const parClassName = clsx('col-xs-12 margin-top-5', {
        'col-sm-6 col-sm-offset-6': isPriceBreakdownVisible,
    });

    const renderShowCaseAd = (
        <LazyComponent
            height={265}
            width={300}
        >
            <div
                className="text-center col-sm-5"
                data-cmp="pricing-details-showcase-ad"
                style={{ position: 'sticky', top: '100px' }}
            >
                <ShowcaseAdSlotContainer />
            </div>
        </LazyComponent>
    );

    const renderPricing = (
        <>
            <PricingDetails
                alignment="left"
                listing={listing}
                showHeading
                showPriceAdvisor={isPriceAdvisorVisible}
                showPriceBreakdown={isPriceBreakdownVisible}
                showDealerAdjustment={isDealerAdjustmentVisible}
                zip={zip}
                emailCTAProps={emailCTAProps}
            />
            {parTile && (
                <PriceAdvisorReport
                    className={parClassName}
                    image={parTile.image}
                    link={parTile.link}
                    title={parTile.tile}
                    linkVisible={parTile.visible}
                    onTileClick={handlePriceAdvisorReportClick}
                    onLinkClick={handlePriceAdvisorReportClick}
                />
            )}
        </>
    );

    const renderBoxContent = (
        <Box
            backgroundColor="brand-primary"
            className="padding-5"
        >
            <Heading
                componentClass="h3"
                className="margin-bottom-4"
                color="primary"
                level={3}
            >
                See how this price measures up
            </Heading>
            {renderPricing}
        </Box>
    );

    if (hidePriceBreakdown || !hasPrice || !enablePriceDetails || !isPriceAdvisorVisible && !isPriceBreakdownVisible) {
        return null;
    }

    return displayPricingDetails && (
        <Section
            alignment="left"
            data-cmp="pricingDetailsSection"
            id="pricingSection"
            uiContext="level-3"
            className="padding-vertical-4 padding-vertical-md-5"
        >
            {isSm ? (
                <div className="row">
                    <div className="col-sm-7">
                        {renderBoxContent}
                    </div>
                    {renderShowCaseAd}
                </div>
            ) : renderBoxContent}
        </Section>
    );
}

export default memo(PricingDetailsContainer);
